import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Dropdown panel rendered in a portal on <body>, positioned under its anchor.
 * Because it lives outside the page layout it is never clipped by a parent with
 * overflow:hidden or covered by the next section.
 */
export default function Popover({ open, anchorRef, onClose, children, minWidth = 260, align = "left", className = "", testId }) {
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = () => {
    const a = anchorRef.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight;
    const width = Math.min(Math.max(r.width, minWidth), vw - 16);
    let left = align === "right" ? r.right - width : r.left;
    left = Math.max(8, Math.min(left, vw - width - 8));
    const below = vh - r.bottom;
    const openUp = below < 280 && r.top > below;
    setPos({ left, width, top: openUp ? undefined : r.bottom + 8, bottom: openUp ? vh - r.top + 8 : undefined, maxH: Math.max(180, (openUp ? r.top : below) - 24) });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onScroll = () => place();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <AnimatePresence>
      {open && pos && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: pos.top !== undefined ? -6 : 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: pos.top !== undefined ? -6 : 6, scale: 0.98 }}
          transition={{ duration: 0.16 }}
          style={{ position: "fixed", left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH, zIndex: 9999 }}
          className={`bg-white rounded-2xl shadow-[0_24px_60px_rgba(42,15,17,0.22)] border border-[color:var(--line)] overflow-hidden flex flex-col ${className}`}
          data-testid={testId}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
